import { useTranslation } from 'react-i18next';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { Button, Stack } from '@hotjar/hotsauce';
import { useGuideCardVisibility } from '@hotjar/webapp/hooks/useGuideCardVisibility';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useCurrentSite } from '@hotjar/site';
import { GuideCard, VideoThumbnail } from '@hotjar/activation-toolkit';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import Thumbnail from './assets/recordings_thumbnail.jpg';

const VIDEO_URL = 'https://www.youtube.com/embed/TkrbsJhtad4?autoplay=1';
const VIDEO_DURATION = '1:24';
const UI_POSITION = 'Recordings Guide';

interface RecordingsGuideCardProps {
  onViewFiltersClick: () => void;
}

export const RecordingsGuideCard = ({
  onViewFiltersClick,
}: RecordingsGuideCardProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { click: trackClick } = useTrack();
  const { isGuideCardVisible, dismissGuideCard } = useGuideCardVisibility('recordings-guide');
  const inPaidPlan = currentSite?.plan !== 'basic' || currentSite?.trial.active;

  const getPlanCopyKey = () => {
    return inPaidPlan ? 'paid' : 'non-paid';
  };

  const handleCTAClick = () => {
    if (inPaidPlan) {
      onViewFiltersClick();
      trackClick('view all filters', UI_POSITION, {});
    } else {
      trackClick('go to free course', UI_POSITION, {});
    }
  };

  const handleSecondaryCTAClick = () => {
    if (inPaidPlan) {
      trackClick('go to free course', UI_POSITION, {});
    } else {
      trackClick('view upgrade options', UI_POSITION, {});
      upsell({
        site: currentSite,
        referrer: 'recordings_guide_card',
      });
    }
  };

  const handleThumbnailClick = () => {
    trackClick('video thumbnail', UI_POSITION, {});
  };

  const handleOnDismiss = () => {
    trackClick('dismiss guide card', UI_POSITION, {});
    dismissGuideCard();
  };

  if (!isGuideCardVisible) return null;

  return (
    <GuideCard
      label={t('activation:recordings-guide-card.label')}
      title={t(`activation:recordings-guide-card.${getPlanCopyKey()}.title`)}
      description={t(`activation:recordings-guide-card.${getPlanCopyKey()}.description`)}
      onDismiss={handleOnDismiss}
      media={
        <VideoThumbnail
          modalContentLabel={UI_POSITION}
          duration={VIDEO_DURATION}
          videoUrl={VIDEO_URL}
          thumbnail={Thumbnail}
          onClick={handleThumbnailClick}
          title={t(`activation:recordings-guide-card.${getPlanCopyKey()}.video-modal-title`)}
        />
      }
      actions={
        <Stack direction="row">
          {inPaidPlan ? (
            <>
              <Button kind="secondary" onClick={handleCTAClick}>
                {t(`activation:recordings-guide-card.${getPlanCopyKey()}.cta`)}
              </Button>
              <Button
                href="https://learning.hotjar.com/courses/how-to-find-insights-in-recordings/view/"
                isExternal
                as="a"
                icon="open_new_window"
                kind="ghost"
                onClick={handleSecondaryCTAClick}
              >
                {t(`activation:recordings-guide-card.${getPlanCopyKey()}.cta-secondary`)}
              </Button>
            </>
          ) : (
            <>
              <Button
                href="https://learning.hotjar.com/courses/how-to-find-insights-in-recordings/view/"
                isExternal
                as="a"
                icon="open_new_window"
                kind="secondary"
                onClick={handleCTAClick}
              >
                {t(`activation:recordings-guide-card.${getPlanCopyKey()}.cta`)}
              </Button>
              <Button kind="ghost" onClick={handleSecondaryCTAClick}>
                {t(`activation:recordings-guide-card.${getPlanCopyKey()}.cta-secondary`)}
              </Button>
            </>
          )}
        </Stack>
      }
    />
  );
};

export const RecordingsGuide = ({
  totalRecordingsCount,
  triggerFiltersModal,
}: {
  totalRecordingsCount: number;
  triggerFiltersModal: () => void;
}): JSX.Element | null => {
  const { data: site } = useCurrentSite();
  const { getState } = useTrackingCode();
  const trackingCodeState = getState(site?.lastData);
  const account = site?.account;
  const recordingsGuideEnabled =
    !!getParameter(window.location.href, `force_recordings_guide`) ||
    (!!account?.id && trackingCodeState.installed && totalRecordingsCount > 24);

  return recordingsGuideEnabled ? (
    <RecordingsGuideCard onViewFiltersClick={triggerFiltersModal} />
  ) : null;
};
